/**
 * Left-column stage list (330px card, per the stage-schedule mockup).
 *
 * Each row: mono start time, SS/H chip (amber = Stage, cyan = Circuit),
 * name, status chip, and a ⚠ icon when the stage has NO start trigger
 * (no start gate, no start time, no corridor route — phones could never
 * begin it). Rows derive from the stages_json ⨯ gates merge done
 * in stageModel.buildStageViews — a stage_index that has gates but no
 * metadata still shows here, unnamed.
 */

import React from 'react';
import { Plus, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import {
  CAR_INTERVAL_OPTIONS,
  STAGE_STATUS_LABEL,
  StageStatus,
  StageView,
  hasStartTrigger,
  stageLabel,
} from './stageModel';

export interface StageListItem {
  view: StageView;
  status: StageStatus;
}

interface StagesListPaneProps {
  items: StageListItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  onAdd: () => void;
  addDisabled?: boolean;
}

function intervalLabel(seconds: number): string {
  const opt = CAR_INTERVAL_OPTIONS.find((o) => o.seconds === seconds);
  if (opt) return opt.label;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** "14.1 km · interval 1:00"-style sub line, from the data we have. */
function subLine(view: StageView): string {
  const parts: string[] = [];
  if (view.type === 'Circuit') {
    if (view.laps != null) parts.push(`${view.laps} laps`);
  } else if (view.carIntervalSeconds != null) {
    parts.push(`interval ${intervalLabel(view.carIntervalSeconds)}`);
  }
  if (view.reverseOf != null) parts.push(`reverse of SS${view.reverseOf + 1}`);
  if (parts.length === 0) {
    parts.push(view.type === 'Circuit' ? 'circuit heat' : 'special stage');
  }
  return parts.join(' · ');
}

const STATUS_CHIP_CLASS: Record<StageStatus, string> = {
  complete: 'border-status-online/40 bg-status-online/10 text-status-online',
  live: 'border-status-live/40 bg-status-live/10 text-status-live',
  scheduled: 'border-accent/30 bg-accent/10 text-accent',
  draft: 'border-dashed border-border-mid text-fg-muted',
};

export const StagesListPane: React.FC<StagesListPaneProps> = ({
  items,
  selectedIndex,
  onSelect,
  onAdd,
  addDisabled,
}) => {
  return (
    <Card className="p-2.5 pb-3.5">
      <CardContent className="p-0">
        <div className="px-1 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-fg-muted">
          Stages · {items.length}
        </div>
        <div className="flex flex-col gap-0.5">
          {items.map(({ view, status }) => {
            const selected = view.index === selectedIndex;
            const noStartTrigger = !hasStartTrigger(view);
            return (
              <button
                key={view.index}
                type="button"
                onClick={() => onSelect(view.index)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded border px-2.5 py-2.5 text-left',
                  selected
                    ? 'border-accent/30 bg-bg-elevated shadow-[inset_3px_0_0_var(--accent-primary)]'
                    : 'border-transparent hover:bg-bg-elevated',
                  status === 'complete' && !selected && 'opacity-55',
                )}
              >
                <span className="w-11 shrink-0 font-mono text-[13px] font-semibold text-fg">
                  {view.startTime ?? '--:--'}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-[13px] font-semibold text-fg">
                    <span
                      className={cn(
                        'shrink-0 rounded-sm px-1.5 py-px font-mono text-[11px] font-bold text-bg',
                        view.type === 'Circuit' ? 'bg-accent-cyan' : 'bg-accent',
                      )}
                    >
                      {stageLabel(view.index, view.type)}
                    </span>
                    <span className={cn('truncate', view.name === '' && 'italic text-fg-muted')}>
                      {view.name === '' ? 'Unnamed stage' : view.name}
                    </span>
                  </span>
                  <span className="mt-px block truncate text-[11px] text-fg-muted">
                    {subLine(view)}
                  </span>
                </span>
                {noStartTrigger && (
                  <TriangleAlert
                    className="size-4 shrink-0 text-status-warning"
                    aria-label="No start trigger — set a start gate, start time, or corridor route"
                  />
                )}
                <span
                  className={cn(
                    'shrink-0 rounded-pill border px-2 py-0.5 text-[10px] font-semibold',
                    STATUS_CHIP_CLASS[status],
                  )}
                >
                  {STAGE_STATUS_LABEL[status]}
                </span>
              </button>
            );
          })}
        </div>
        <Button
          type="button"
          variant="outline"
          className="mt-2.5 w-full justify-center gap-1"
          onClick={onAdd}
          disabled={addDisabled}
        >
          <Plus className="size-4" />
          Add stage
        </Button>
      </CardContent>
    </Card>
  );
};

export default StagesListPane;
